import { omit } from 'lodash'
import { addMinutes, parse } from 'date-fns'
import { POST } from '@/api'
import { wait } from '@/utils'
import { toDate } from '@/utils/date'
import { syncFromGnavi, syncFromOtherCompany, isResraku } from '@/resources/reservation/syncTypeMap'
import { isWalkin, isBlock } from '@/resources/reservation/typeMap'

const initialReserve = {
  id: null,
  receiptDatetime: null,
  reserveDatetime: null,
  utilizationTime: null,
  resetTime: null,
  status: null,
  customer: {
    id: null,
    lastNameKana: null,
    firstNameKana: null,
    lastName: null,
    firstName: null,
    tel: null,
    tel1DuplicationFlag: false,
    tel2: null,
    tel2DuplicationFlag: false,
    officeName: null,
    mail1: null,
    mail2: null,
    visitCount: 0,
    syncMemberType: null,
  },
  reserveName: null,
  adults: 0,
  children: 0,
  tableTypeLabel: null,
  tables: [],
  tableIds: [],
  courses: [],
  type: 1,
  media: 0,
  purpose: 0,
  settlement: 0,
  remarks: null,
  freehandMemo: null,
  tags: [],
  syncType: null,
  syncRsvId: null,
  privRoomType: null,
  smokingType: null,
  tableType: null,
  staffName: null,
}

export default {
  namespaced: true,
  state: {
    reservation: { ...initialReserve },
    busy: false,
    cautionType: null,
  },
  getters: {
    isReservedByGnavi({ reservation: { syncType } }) {
      return !!(syncType && syncFromGnavi[syncType])
    },
    /** 店舗移動可能かどうか */
    canChangeStore: ({ reservation }, { isReservedByGnavi }) => {
      return (
        !isReservedByGnavi &&
        !isResraku(reservation.syncType) &&
        ![5, 6].includes(reservation.syncSource) &&
        !isBlock(reservation.type)
      )
    },
    reserveName({ reservation: { reserveName: _reserveName, customer, type, syncType } }) {
      if (_reserveName) return _reserveName
      const { lastName, lastNameKana: lastKana, firstName, firstNameKana: firstKana, tel, tel1, tel2 } = customer
      const prefix = syncFromOtherCompany[syncType] || ''
      const name = lastName && firstName ? `${lastName} ${firstName}` : lastName || firstName || ''
      const kana = lastKana && firstKana ? `${lastKana} ${firstKana}` : lastKana || firstKana || ''
      const rName = name || kana || tel || tel1 || tel2
      return prefix && rName
        ? `${prefix}_${rName}`
        : prefix || rName || ((isWalkin(type) || syncType === 3 || syncType === 8) && 'ウォークイン') || ''
    },
  },
  mutations: {
    busy(state, busy) {
      state.busy = !!busy
    },
    clear(state) {
      state.reservation = { ...initialReserve }
    },
    set(state, reserve) {
      state.reservation = {
        ...initialReserve,
        ...reserve,
        customer: {
          ...initialReserve.customer,
          ...reserve.customer,
        },
      }
    },
    updateRestoreCaution(state, cautionType) {
      state.cautionType = cautionType
    },
  },
  actions: {
    async get(store, { id, alertType, includeCancel }) {
      const { commit, dispatch, state } = store
      commit('busy', true)

      const { status, message, data } = await POST('/api/web/reservation/detail', {
        reservationId: id,
        // 許容するalertTypeの一覧 (https://gnavi-work.gnavi.co.jp/wiki/x/pHd-AQ) を参照
        alertType: [-1, 5, 6, 7, 8, 10, 11, 12, 13, 15, 16, 17, 24, 25, 26, 35, 37, 38].includes(alertType)
          ? alertType
          : null,
        includeCancel,
      })

      if (status === 200) {
        const {
          customer,
          reservation: { reserveDate, reserveTime, receiptDate, receiptTime, ...reservation },
        } = data
        if (customer?.id) {
          const cust = await dispatch('customers/detail/get', { id: customer.id }, { root: true })
          if (cust) customer.visitCount = cust.visitCount // TODO 予約詳細APIから返してもらった方がよい
        } else {
          commit('customers/detail/set', null, { root: true })
        }
        const [hour, minute] = reserveTime.split(/[:：]/)
        commit('set', {
          customer,
          ...reservation,
          receiptDatetime: parse(`${receiptDate}T${receiptTime.replace('：', ':')}`),
          reserveDatetime: addMinutes(reserveDate, hour * 60 + +minute),
          tableIds: reservation.tables.map(table => table.id),
        })
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200 ? state.reservation : null
    },
    async changeStatus(store, { id, visitStatus }) {
      const { commit, state } = store
      if (state.busy) return false
      commit('busy', true)

      // 連続操作するとAPI処理が追いつかないので遅延を付与
      await wait(1000)

      const { status, message } = await POST('/api/web/reservation/status/update', {
        reservationId: id,
        status: visitStatus,
      })

      if (status !== 200) {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
    async settle(store, staffId) {
      const { commit, state } = store
      if (state.busy) return false
      commit('busy', true)

      const { status, message } = await POST('/api/web/reservation/settle', {
        reservationId: state.reservation.id,
        staffId,
      })

      if (status !== 200) {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
    loadReserveData({ commit, rootState }) {
      // TODO: reserveDataを使わなくなったら消す
      const {
        customer,
        reservation: { reserveDate, table, ...reserve },
      } = rootState.reserveData
      commit('set', {
        ...omit(reserve, []),
        customer,
        reserveDatetime: reserveDate.year ? toDate(reserveDate) : null,
        tableIds: table
          .split(',')
          .filter(id => id)
          .map(id => +id),
      })
    },
    async restoreReservation(store, { reserveId, staffId }) {
      const { commit, state } = store
      if (state.busy) return false
      commit('busy', true)

      const { status, message, data } = await POST('/api/web/reservation/restore', {
        reservationId: reserveId,
        staffId,
      })

      if (status === 200) {
        commit('updateRestoreCaution', data.cautionType)
      } else {
        commit('app/error', { status, message }, { root: true })
      }

      commit('busy', false)
      return status === 200
    },
  },
}
